import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  getDocs,
  where,
  Timestamp,
  addDoc,
  doc,
  writeBatch,
  serverTimestamp,
  updateDoc,
  arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase';
import { Route, RouteGroup, Client, Order, Transportista } from '../models';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import { styled } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import {
  Person as PersonIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { addMonths } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import DatePicker from 'react-datepicker';
import { es } from 'date-fns/locale';

// Define un nuevo tipo para incluir la fecha de unificación
interface SelectedRouteGroup {
  routeGroup: RouteGroup;
  dateUnification: Date;
}

// Custom TabPanel component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#00b4d8',
    },
    '&:hover fieldset': {
      borderColor: '#00b4d8',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00b4d8',
    },
  },
  '& .MuiInputLabel-root': {
    color: '#00b4d8',
  },
}));

// Define styled components
const DarkOverlay = styled(Box)({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  backdropFilter: 'blur(4px)',
  zIndex: 9998,
  animation: 'fadeIn 0.3s ease-out',
  '@keyframes fadeIn': {
    '0%': {
      opacity: 0,
    },
    '100%': {
      opacity: 1,
    },
  },
});

const NotificationPopup = styled(Box)({
  position: 'fixed',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  backgroundColor: 'rgba(255, 255, 255, 0.98)',
  padding: '32px 48px',
  borderRadius: '24px',
  boxShadow: '0 8px 40px rgba(0, 0, 0, 0.25)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  alignItems: 'center',
  gap: '24px',
  zIndex: 9999,
  // minWidth: '500px',
  animation: 'slideIn 0.5s ease-out',
  '@keyframes slideIn': {
    '0%': {
      transform: 'translate(-50%, -40%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translate(-50%, -50%)',
      opacity: 1,
    },
  },
});

const AnimatedEmoji = styled('span')({
  display: 'inline-block',
  animation: 'bounce 1s ease infinite',
  fontSize: '2.5rem',
  '@keyframes bounce': {
    '0%, 100%': {
      transform: 'translateY(0)',
    },
    '50%': {
      transform: 'translateY(-8px)',
    },
  },
});

const columnNames = {
  routeGroupName: 'Grupo de Ruta',
  clientName: 'Cliente',
  orderStatus: 'Estado',
  orderType: 'Tipo de Pedido',
  date: 'Fecha',
  notes: 'Notas',
};

// Define a more flexible type for route union object
type RouteUnion =
  | { routes?: Route | Route[]; date?: Date | Timestamp; isUnificationFlag?: boolean }
  | Route[]
  | [Route | Route[], Date, boolean];

// Type guard and conversion helpers
const isRoute = (item: any): item is Route =>
  item && typeof item === 'object' && 'id' in item;

const isDate = (item: any): item is Date =>
  item instanceof Date;

const isIterable = (obj: any): boolean =>
  obj !== null && typeof obj[Symbol.iterator] === 'function';

const convertToRouteArray = (input: Route | Route[] | any): Route[] => {
  if (Array.isArray(input)) return input;
  if (isRoute(input)) return [input];
  // console.warn('Unable to convert to route array:', input);
  return [];
};

const convertToDate = (input: any): Date => {
  if (input instanceof Date) return input;
  if (input instanceof Timestamp) return input.toDate();
  if (typeof input === 'string' || typeof input === 'number') return new Date(input);
  // console.warn('Unable to convert to date:', input);
  return new Date();
};

const processRouteUnion = (routeUnion: any): [Route[], Date, boolean] | null => {
  // Check if routeUnion is undefined or null
  if (routeUnion === undefined || routeUnion === null) {
    // console.warn('❌ Route Union is undefined or null');
    return null;
  }

  // If routeUnion is already an array with correct structure, return it
  if (
    Array.isArray(routeUnion) &&
    routeUnion.length === 3 &&
    Array.isArray(routeUnion[0]) &&
    (routeUnion[1] instanceof Date || routeUnion[1] instanceof Timestamp) &&
    typeof routeUnion[2] === 'boolean'
  ) {

    return routeUnion as [Route[], Date, boolean];
  }

  // If routeUnion is an object, transform it
  if (typeof routeUnion === 'object' && routeUnion !== null) {
    // Check for required properties
    const requiredProps = ['order', 'clientId', 'status', 'id'];
    const missingProps = requiredProps.filter(prop => !(prop in routeUnion));

    if (missingProps.length > 0) {
      // console.warn(`❌ Invalid route union: Missing properties ${missingProps.join(', ')}`, routeUnion);
      return null;
    }

    // Create a route from the order, filling in missing required fields
    const route: Route = {
      id: routeUnion.id,
      clientId: routeUnion.clientId,
      status: routeUnion.status || 'pending',
      order: routeUnion.order || { items: [] },
      tipo_pedido: 'Regular', // Default value
      fecha_pedido: new Date(), // Current date as default
      routeGroupId: '', // Empty string as default, might need to be updated
    };

    // Use current date as the route date
    const routeDate = new Date();

    // Add logging for isUnificationFlag
    const isUnificationFlag = (() => {
      // console.log('🚩 Checking Unification Flag:', {
      //   status: routeUnion.status,
      //   hasUnificationFlag: 'unificationFlag' in routeUnion,
      //   unificationFlagValue: routeUnion.unificationFlag
      // });

      // Check if unificationFlag exists and is a boolean
      if ('isUnificationFlag' in routeUnion) {
        return typeof routeUnion.isUnificationFlag === 'boolean'
          ? routeUnion.isUnificationFlag
          : false;
      }

      // Fallback to status-based logic
      return routeUnion.status === 'pending';
    })();


    return [[route], routeDate, isUnificationFlag];
  }

  // console.warn('❌ Invalid route union: Not an array or object', routeUnion);
  return null;
};

// const isValidRouteUnion = (routeUnion: any): boolean => {
//   return processRouteUnion(routeUnion) !== null;
// };

const areDatesEqual = (date1?: Date | Timestamp | null, date2?: Date | Timestamp | null): boolean => {
  if (!date1 || !date2) return false;

  const normalizedDate1 = date1 instanceof Timestamp ? date1.toDate() : date1;
  const normalizedDate2 = date2 instanceof Timestamp ? date2.toDate() : date2;

  return (
    normalizedDate1.getFullYear() === normalizedDate2.getFullYear() &&
    normalizedDate1.getMonth() === normalizedDate2.getMonth() &&
    normalizedDate1.getDate() === normalizedDate2.getDate()
  );
};

const startOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};

const endOfDay = (date: Date) => {
  const newDate = new Date(date);
  newDate.setHours(23, 59, 59, 999);
  return newDate;
};

const UnificationRoutes: React.FC = () => {
  const [routes, setRoutes] = useState<Route[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState<keyof Route>('fecha_pedido');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [multiRouteDays, setMultiRouteDays] = useState<{
    date: Date;
    routeGroups: RouteGroup[];
  }[]>([]);
  const [currentRouteGroupIndex, setCurrentRouteGroupIndex] = useState(0);
  const [viewMode, setViewMode] = useState(0);

  const [isUnificationDialogOpen, setIsUnificationDialogOpen] = useState(false);
  const [selectedRouteGroups, setSelectedRouteGroups] = useState<SelectedRouteGroup[]>([]);
  const [availableRouteGroups, setAvailableRouteGroups] = useState<RouteGroup[]>([]);

  const [clientsMap, setClientsMap] = useState<Map<string, Client>>(new Map());

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [showNotification, setShowNotification] = useState(false);
  const [createShowNotification, setCreateShowNotification] = useState(false);

  const [selectedDayData, setSelectedDayData] = useState<{ date: Date; routeGroups: RouteGroup[] } | null>(null);
  const navigate = useNavigate();
  const [selectedTabDate, setSelectedTabDate] = useState<Date | null>(null); // Nuevo estado para la fecha del tab actual
  const [isSaveButtonEnabled, setIsSaveButtonEnabled] = useState(false); // New state for button enable/disable
  const [transportistas, setTransportistas] = useState<Transportista[]>([]);
  const [selectedTransportista, setSelectedTransportista] = useState<Transportista | null>(null);
  const [isRefreshRoutesDialogOpen, setIsRefreshRoutesDialogOpen] = useState(false);
  const [refreshDateRange, setRefreshDateRange] = useState<[Date | null, Date | null]>([new Date(), addMonths(new Date(), 1)]);

  useEffect(() => {
    const fetchTransportistas = async () => {
      const transportistasRef = collection(db, 'transportistas');
      const snapshot = await getDocs(transportistasRef);
      const transportistasData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Transportista[];
      setTransportistas(transportistasData);
    };

    fetchTransportistas();
  }, []);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const clientsCollection = collection(db, 'clients');
        const clientsSnapshot = await getDocs(clientsCollection);

        const clientsMapTemp = new Map<string, Client>();
        clientsSnapshot.forEach((doc) => {
          const clientData = { id: doc.id, ...doc.data() } as Client;
          clientsMapTemp.set(doc.id, clientData);
        });

        setClientsMap(clientsMapTemp);
      } catch (error) {
        // console.error('Error fetching clients:', error);
      }
    };

    fetchClients();
  }, []);

  // Actualiza el onClick del botón existente
  const handleOpenRefreshDialog = () => {
    setIsRefreshRoutesDialogOpen(true);
  };

  const handleCloseRefreshDialog = () => {
    setIsRefreshRoutesDialogOpen(false);
  };

  const fetchUnificationRoutes = useCallback(async () => {
    try {
      const processedMultiRouteDays: {
        date: Date;
        routeGroups: RouteGroup[];
      }[] = [];

      // Calculate date range (tomorrow to one month in the future)
      const currentDate = new Date();
      const startDate = new Date(currentDate);
      startDate.setHours(0, 0, 0, 0);
      // startDate.setDate(startDate.getDate());
      const oneMonthFromNow = new Date(startDate);
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1); // Change from +2 to +1 for one month
      oneMonthFromNow.setHours(23, 59, 59, 999); // End at end of last day

      // Generate all dates in the range
      const daysInMonth: Date[] = [];
      const currentIterationDate = new Date(startDate);

      while (currentIterationDate <= oneMonthFromNow) {
        daysInMonth.push(new Date(currentIterationDate));
        currentIterationDate.setDate(currentIterationDate.getDate() + 1);
      }

      // Fetch route groups from Firestore using query method
      const routeGroupsRef = collection(db, 'routeGroups');
      const routeGroupsQuery = query(
        routeGroupsRef,
        where('routeType', 'in', ['weekly', 'biweekly', 'monthly', 'bimonthly'])
      );
      const routeGroupsSnapshot = await getDocs(routeGroupsQuery);

      // Process each day in the month
      for (const currentDate of daysInMonth) {
        // Fetch and process route groups for the current date
        const matchingRouteGroups = await Promise.all(
          routeGroupsSnapshot.docs.map(async (doc) => {
            const routesUnionQuery = query(
              collection(db, `routeGroups/${doc.id}/routesUnion`),
              where('date', '>=', startOfDay(startDate)),
              where('date', '<=', endOfDay(oneMonthFromNow))
            );
            const routesUnionSnapshot = await getDocs(routesUnionQuery);

            // Process routesUnion, filtering for valid unification routes
            const processedRoutesUnion = routesUnionSnapshot.docs
              .filter(unionDoc => {
                const data = unionDoc.data();
                const isValidUnificationRoute = data.isUnificationFlag === true;

                return isValidUnificationRoute;
              })
              .map(unionDoc => {
                const data = unionDoc.data();
                return [
                  data.routes as Route[],
                  data.date as Date,
                  true // Always true since we filtered above
                ] as [Route[], Date, boolean];
              });

            // If no valid unification route documents, skip this route group
            if (processedRoutesUnion.length === 0) {
              return null;
            }

            // Combine routes from all matching routesUnion
            const combinedRoutes = processedRoutesUnion.flatMap(item => item[0]);

            return {
              id: doc.id,
              routes: combinedRoutes,
              routesUnion: processedRoutesUnion,
              name: doc.data().name || '',
              dayOfWeek: doc.data().dayOfWeek || 0,
              weekOfMonth: doc.data().weekOfMonth || 0,
              createdAt: doc.data().createdAt || new Timestamp(0, 0),
              routeType: 'Unification'
            } as RouteGroup;
          })
        );

        // Filter out null results and route groups without routes
        const filteredRouteGroups = matchingRouteGroups
          .filter((routeGroup): routeGroup is RouteGroup =>
            routeGroup !== null && routeGroup.routes.length > 0
          );

        // Filter route groups to only show those with valid unification routes
        const validRouteGroups = filteredRouteGroups
          .filter(group => group !== null && group.routesUnion.length > 0);

        // If there are matching route groups, add to multi-route days
        if (validRouteGroups.length > 0) {
          processedMultiRouteDays.push({
            date: currentDate,
            routeGroups: validRouteGroups
          });
        }
      }

      setMultiRouteDays(processedMultiRouteDays);
      setLoading(false);
    } catch (error) {
      // console.error('❌ Error in fetchUnificationRoutes:', error);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUnificationRoutes();
  }, []);

  const handleRefresh = useCallback(async () => {
    setShowNotification(true);
    setIsRefreshRoutesDialogOpen(false);

    try {
      const processedMultiRouteDays: {
        date: Date;
        routeGroups: RouteGroup[];
      }[] = [];

      // Usar las fechas del refreshDateRange y asegurar que no sean null
      const startDate = refreshDateRange[0] || new Date();
      const endDate = refreshDateRange[1] || addMonths(new Date(), 1);

      // Configurar las horas para el rango completo
      const startOfRange = startOfDay(startDate);
      const endOfRange = endOfDay(endDate);

      // Generate all dates in the range
      const daysInRange: Date[] = [];
      const currentIterationDate = new Date(startDate);

      while (currentIterationDate <= endOfRange) {
        daysInRange.push(new Date(currentIterationDate));
        currentIterationDate.setDate(currentIterationDate.getDate() + 1);
      }

      // Fetch route groups from Firestore
      const routeGroupsRef = collection(db, 'routeGroups');
      const routeGroupsQuery = query(
        routeGroupsRef,
        where('routeType', 'in', ['weekly', 'biweekly', 'monthly', 'bimonthly'])
      );
      const routeGroupsSnapshot = await getDocs(routeGroupsQuery);

      // Process each day in the range
      for (const currentDate of daysInRange) {
        const matchingRouteGroups = await Promise.all(
          routeGroupsSnapshot.docs.map(async (doc) => {
            // Query routesUnion with date range constraints
            const routesUnionQuery = query(
              collection(db, `routeGroups/${doc.id}/routesUnion`),
              where('date', '>=', startOfRange),
              where('date', '<=', endOfRange)
            );
            const routesUnionSnapshot = await getDocs(routesUnionQuery);

            // Filter routesUnion documents for the current date
            const processedRoutesUnion = routesUnionSnapshot.docs
              .filter(unionDoc => {
                const data = unionDoc.data();
                const routeDate = data.date instanceof Timestamp ?
                  data.date.toDate() :
                  new Date(data.date);

                // Comparar solo la fecha (sin hora)
                return routeDate.toDateString() === currentDate.toDateString();
              })
              .map(unionDoc => {
                const data = unionDoc.data();
                return [
                  data.routes as Route[],
                  data.date instanceof Timestamp ? data.date.toDate() : new Date(data.date),
                  false
                ] as [Route[], Date, boolean];
              });

            if (processedRoutesUnion.length === 0) {
              return null;
            }

            const combinedRoutes = processedRoutesUnion.flatMap(item => item[0]);

            return {
              id: doc.id,
              routes: combinedRoutes,
              routesUnion: processedRoutesUnion,
              routesUnionSnapshot: routesUnionSnapshot,
              name: doc.data().name || '',
              dayOfWeek: doc.data().dayOfWeek || 0,
              weekOfMonth: doc.data().weekOfMonth || 0,
              createdAt: doc.data().createdAt || new Timestamp(0, 0),
              routeType: 'Unification'
            } as RouteGroup & { routesUnionSnapshot: any };
          })
        );

        const filteredRouteGroups = matchingRouteGroups
          .filter((routeGroup): routeGroup is RouteGroup & { routesUnionSnapshot: any } =>
            routeGroup !== null && routeGroup.routes.length > 0
          );

        const validRouteGroups = filteredRouteGroups
          .filter(group => group !== null && group.routesUnion.length > 0);

        if (validRouteGroups.length > 1) {
          validRouteGroups.forEach(async group => {
            group.routesUnion.forEach(async (routeUnion, index) => {
              routeUnion[2] = true;
              const unionDocRef = doc(db, `routeGroups/${group.id}/routesUnion`,
                group.routesUnionSnapshot.docs[index].id);
              await updateDoc(unionDocRef, { isUnificationFlag: true });
            });
          });
        }

        if (validRouteGroups.length > 0) {
          processedMultiRouteDays.push({
            date: currentDate,
            routeGroups: validRouteGroups
          });
        }
      }

      setMultiRouteDays(processedMultiRouteDays);
      setLoading(false);

      // Resetear el rango de fechas al valor por defecto después de la actualización
      setRefreshDateRange([new Date(), addMonths(new Date(), 1)]);
    } catch (error) {
      setLoading(false);
    } finally {
      setShowNotification(false);
    }
  }, [refreshDateRange]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: keyof Route) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const sortedRoutes = useMemo(() => {
    return [...routes].sort((a, b) => {
      const isAsc = order === 'asc';
      const getValue = (route: Route) => {
        switch (orderBy) {
          case 'fecha_pedido':
            return route.fecha_pedido instanceof Timestamp
              ? route.fecha_pedido.toDate().getTime()
              : route.fecha_pedido.getTime();
          case 'status':
            return route.status;
          default:
            return route.id || '';
        }
      };

      const valueA = getValue(a);
      const valueB = getValue(b);

      return (valueA < valueB ? -1 : 1) * (isAsc ? 1 : -1);
    });
  }, [routes, order, orderBy]);

  const paginatedRoutes = useMemo(() => {
    const startIndex = page * rowsPerPage;
    return sortedRoutes.slice(startIndex, startIndex + rowsPerPage);
  }, [sortedRoutes, page, rowsPerPage]);

  const handleViewDetails = (route: Route) => {
    // Implement view details logic
  };

  const handleEditRoute = (route: Route) => {
    // Implement edit route logic
  };

  const handleDeleteRoute = (routeId?: string) => {
    // Implement delete route logic
  };

  const handleRouteGroupChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentRouteGroupIndex(newValue);
  };

  const a11yProps = (index: number) => {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const fetchAvailableRouteGroups = useCallback(async () => {
    try {
      const routeGroupsRef = collection(db, 'routeGroups');
      const q = query(
        routeGroupsRef
        // ,where('unificationFlag', '==', true)
      );
      const querySnapshot = await getDocs(q);
      const fetchedRouteGroups = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      } as RouteGroup));

      setAvailableRouteGroups(fetchedRouteGroups);
    } catch (error) {
      // console.error('Error fetching route groups:', error);
      // toast.error('No se pudieron cargar los grupos de ruta');
    }
  }, []);

  // const handleRouteGroupSelect = (routeGroup: RouteGroup) => {
  //   setSelectedRouteGroups(prevSelected => {
  //     // Check if the route group is already selected
  //     const isAlreadySelected = prevSelected.some(rg => rg.id === routeGroup.id);

  //     if (isAlreadySelected) {
  //       // If already selected, remove it
  //       return prevSelected.filter(rg => rg.id !== routeGroup.id);
  //     } else {
  //       // If not selected, add it
  //       return [...prevSelected, routeGroup];
  //     }
  //   });
  // };

  // const handleRouteGroupSelect = (routeGroup: RouteGroup, date: Date) => {
  //   setSelectedRouteGroups(prevSelected => {
  //     // Check if the route group is already selected
  //     const isAlreadySelected = prevSelected.some(rg => rg.id === routeGroup.id && rg.routesUnion.some(([_, routeDate]) => routeDate.toDateString() === date.toDateString()));

  //     if (isAlreadySelected) {
  //       // If already selected, remove it
  //       return prevSelected.filter(rg => rg.id !== routeGroup.id || !rg.routesUnion.some(([_, routeDate]) => routeDate.toDateString() === date.toDateString()));
  //     } else {
  //       // If not selected, add it
  //       return [...prevSelected, routeGroup];
  //     }
  //   });
  // };

  const handleRouteGroupSelect = (routeGroup: RouteGroup, date: Date) => {
    setSelectedRouteGroups(prevSelected => {
      // Normaliza la fecha seleccionada
      const selectedDateString = date.toDateString();

      // Verifica si el grupo de rutas tiene un routesUnion que coincide con la fecha
      const hasMatchingRouteUnion = routeGroup.routesUnion.some(([_, routeDate]) => {
        const normalizedRouteDate = routeDate instanceof Timestamp ? routeDate.toDate() : routeDate;
        return normalizedRouteDate && normalizedRouteDate.toDateString() === selectedDateString;
      });

      if (hasMatchingRouteUnion) {
        // Si hay un match, verifica si ya está seleccionado
        const isAlreadySelected = prevSelected.some(selected =>
          selected.routeGroup.id === routeGroup.id && selected.dateUnification.toDateString() === selectedDateString
        );

        if (isAlreadySelected) {
          // Si ya está seleccionado, lo elimina
          return prevSelected.filter(selected =>
            selected.routeGroup.id !== routeGroup.id || selected.dateUnification.toDateString() !== selectedDateString
          );
        } else {
          // Si no está seleccionado, lo agrega
          setSelectedDayData({ date, routeGroups: [routeGroup] }); // Set the selected day data
          return [...prevSelected, { routeGroup, dateUnification: date }];
        }
      } else {
        // Si no hay match, no se agrega nada
        return prevSelected;
      }
    });
  };


  // const createUnifiedRouteGroup = async (date: Date) => {
  //   if (selectedRouteGroups.length < 2) {
  //     console.error('At least two route groups must be selected');
  //     return;
  //   }

  //   try {
  //     // Collect all routes from selected route groups
  //     const allRoutes: Route[] = [];
  //     const routeGroupIds: string[] = [];

  //     selectedRouteGroups.forEach(selected => {
  //       const routeGroup = selected.routeGroup;

  //       // Collect routes from routesUnion
  //       const routes = routeGroup.routesUnion?.reduce((acc: Route[], routeUnion) => {
  //         const processedRouteUnion = processRouteUnion(routeUnion);
  //         if (processedRouteUnion) {
  //           const [routes, _, isUnificationFlag] = processedRouteUnion;
  //           if (isUnificationFlag) {
  //             return [...acc, ...routes];
  //           }
  //         }
  //         return acc;
  //       }, []) || [];

  //       allRoutes.push(...routes);
  //       if (routeGroup.id) routeGroupIds.push(routeGroup.id);
  //     });

  //     // Create a new RouteGroup with a unique ID
  //     const newRouteGroup: RouteGroup = {
  //       id: doc(collection(db, 'routeGroups')).id, // Generate a unique ID
  //       name: `Unified Group - ${new Date().toLocaleDateString()}`, // Name for the new group
  //       dayOfWeek: new Date().getDay(), // Set the current day of the week
  //       weekOfMonth: Math.ceil(new Date().getDate() / 7), // Calculate the week of the month
  //       createdAt: new Date(),
  //       routes: [], 
  //       routesUnion: [[allRoutes, date, true]], // Wrap the tuple in an array
  //       unificationFlag: true
  //     };
  //     console.log('New Route Group:', newRouteGroup);
  //     // Here you would typically add the new RouteGroup to your database
  //     const routeGroupsCollection = collection(db, 'routeGroups');
  //     await addDoc(routeGroupsCollection, newRouteGroup);

  //     // Reset selection and refresh data
  //     setSelectedRouteGroups([]);
  //     alert('Ruta unificada creada exitosamente');
  //   } catch (error) {
  //     console.error('Error creating unified route group:', error);
  //     alert('Error al crear la ruta unificada');
  //   }
  // };

  // const createUnifiedRouteGroup = async (date: Date) => {
  //   if (selectedRouteGroups.length < 2) {
  //     console.error('At least two route groups must be selected');
  //     return;
  //   }

  //   try {
  //     // Collect all routes from selected route groups
  //     const allRoutes: Route[] = [];
  //     const routeGroupIds: string[] = [];

  //     selectedRouteGroups.forEach(selected => {
  //       const routeGroup = selected.routeGroup;

  //       // Validate date match
  //       const matchingRoutes = routeGroup.routesUnion?.filter(routeUnion => {
  //         const processedRouteUnion = processRouteUnion(routeUnion);
  //         if (processedRouteUnion) {
  //           const [_, routeDate] = processedRouteUnion;
  //           return areDatesEqual(routeDate, date);
  //         }
  //         return false;
  //       }) || [];

  //       // Collect routes from matching routesUnion
  //       matchingRoutes.forEach(routeUnion => {
  //         const processedRouteUnion = processRouteUnion(routeUnion);
  //         if (processedRouteUnion) {
  //           const [routes, _, isUnificationFlag] = processedRouteUnion;
  //           if (isUnificationFlag) {
  //             routes.forEach(route => {
  //               // Check for duplicates based on clientId
  //               const existingRouteIndex = allRoutes.findIndex(r => r.clientId === route.clientId);
  //               if (existingRouteIndex !== -1) {
  //                 // Merge orders if clientId is the same
  //                 allRoutes[existingRouteIndex].order.items.push(...route.order.items);
  //               } else {
  //                 allRoutes.push(route);
  //               }
  //             });
  //           }
  //         }
  //       });

  //       if (routeGroup.id) routeGroupIds.push(routeGroup.id);
  //     });

  //     // Aplanar la estructura de routesUnion
  //     const routesUnionData: [Route[], Date, boolean][] = allRoutes.map(route => (
  //       [[route], date, true] // Wrap the route in an array to match the tuple structure
  //     ));

  //     // Create a new RouteGroup with a unique ID
  //     const newRouteGroup: RouteGroup = {
  //       id: doc(collection(db, 'routeGroups')).id, // Generate a unique ID
  //       name: `Unified Group - ${new Date().toLocaleDateString()}`, // Name for the new group
  //       dayOfWeek: new Date().getDay(), // Set the current day of the week
  //       weekOfMonth: Math.ceil(new Date().getDate() / 7), // Calculate the week of the month
  //       createdAt: new Date(),
  //       routes: [],
  //       routesUnion: routesUnionData,
  //       unificationFlag: true
  //     };
  //     console.log('New Route Group:', newRouteGroup);

  //     // Check the structure of newRouteGroup before adding
  //     // Ensure all fields are of the correct type and structure
  //     // Example: Check if routesUnion is structured correctly
  //     if (!Array.isArray(newRouteGroup.routesUnion) || newRouteGroup.routesUnion.some(item => !Array.isArray(item))) {
  //       console.error('Invalid routesUnion structure:', newRouteGroup.routesUnion);
  //       return; // Exit if the structure is invalid
  //     }

  //     // Convert createdAt to a Date object if it's a string
  //     if (typeof newRouteGroup.createdAt === 'string') {
  //       newRouteGroup.createdAt = new Date(newRouteGroup.createdAt);
  //     }

  //     // Check if routesUnion is structured correctly
  //     if (!Array.isArray(newRouteGroup.routesUnion) || newRouteGroup.routesUnion.some(item => !Array.isArray(item))) {
  //       console.error('Invalid routesUnion structure:', newRouteGroup.routesUnion);
  //       return; // Exit if the structure is invalid
  //     }

  //     // Here you would typically add the new RouteGroup to your database
  //     const routeGroupsCollection = collection(db, 'routeGroups');
  //     try {
  //       await addDoc(routeGroupsCollection, newRouteGroup);
  //     } catch (error) {
  //       console.error('Error adding document:', error); // Log the error for debugging
  //     }

  //     // Reset selection and refresh data
  //     setSelectedRouteGroups([]);
  //     alert('Ruta unificada creada exitosamente');
  //   } catch (error) {
  //     console.error('Error creating unified route group:', error);
  //     alert('Error al crear la ruta unificada');
  //   }
  // };

  const createUnifiedRouteGroup = async (date: Date) => {
    setCreateShowNotification(true);

    if (selectedRouteGroups.length < 2) {
      // console.error('At least two route groups must be selected');
      return;
    }

    try {
      const batch = writeBatch(db); // Crear un batch para las operaciones en Firestore

      // Collect all routes from selected route groups
      const allRoutes: Route[] = [];
      const routeGroupIds: string[] = [];

      selectedRouteGroups.forEach(selected => {
        const routeGroup = selected.routeGroup;

        // Validate date match
        const matchingRoutes = routeGroup.routesUnion?.filter(routeUnion => {
          const processedRouteUnion = processRouteUnion(routeUnion);
          if (processedRouteUnion) {
            const [routes, routeDate] = processedRouteUnion;
            // console.log('Processed Route Union:', processedRouteUnion); // Debugging
            // console.log('Route Date:', routeDate); // Debugging
            // console.log('Date to Compare:', date); // Debugging
            return areDatesEqual(routeDate, date); // Ensure both are defined
          }
          return false;
        }) || [];

        // console.log('Matching Routes:', matchingRoutes); // Debugging

        // Collect routes from matching routesUnion
        matchingRoutes.forEach(routeUnion => {
          const processedRouteUnion = processRouteUnion(routeUnion);
          if (processedRouteUnion) {
            const [routes, _, isUnificationFlag] = processedRouteUnion;
            if (isUnificationFlag) {
              // console.log('Routes to be added:', routes); // Debugging
              routes.forEach(route => {
                // Check for duplicates based on clientId
                const existingRouteIndex = allRoutes.findIndex(r => r.clientId === route.clientId);
                if (existingRouteIndex !== -1) {
                  // Merge orders if clientId is the same
                  route.order.items.forEach(newItem => {
                    const existingItemIndex = allRoutes[existingRouteIndex].order.items.findIndex(
                      existingItem => existingItem.inventoryItemId === newItem.inventoryItemId
                    );

                    if (existingItemIndex !== -1) {
                      // If item exists, sum the quantity
                      allRoutes[existingRouteIndex].order.items[existingItemIndex].quantity += newItem.quantity;
                    } else {
                      // If item doesn't exist, push the new item
                      allRoutes[existingRouteIndex].order.items.push(newItem);
                    }
                  });
                } else {
                  allRoutes.push(route);
                }
              });
            }
          }
        });

        if (routeGroup.id) routeGroupIds.push(routeGroup.id);
      });

      // console.log('All Routes Collected:', allRoutes); // Debugging

      // Convert routesUnion to a valid structure
      const routesUnionData = allRoutes.map(route => ({
        routes: [route], // Wrap the route in an array
        date: Timestamp.fromDate(date), // Convert Date to Timestamp
        isUnificationFlag: true
      }));

      // console.log('Routes Union Data:', routesUnionData); // Debugging

      // Create a new RouteGroup with a unique ID
      const newRouteGroupRef = doc(collection(db, 'routeGroups')); // Generate a unique ID
      const routeGroupData = {
        id: newRouteGroupRef.id,
        name: `Ruta Unificada - ${date.toLocaleDateString()}`, // Name for the new group
        // dayOfWeek: date.getDay(), // Set the current day of the week
        // weekOfMonth: Math.ceil(date.getDate() / 7), // Calculate the week of the month
        createdAt: date ? Timestamp.fromDate(date) : null,
        // routes: [],
        // routesUnion: routesUnionData, // Use the new structure
        routeType: 'Unification',
        unificationFlag: true
      };

      // Link transportista if selected
      if (selectedTransportista?.id) {
        const transportistaRef = doc(db, 'transportistas', selectedTransportista.id);
        await updateDoc(transportistaRef, {
          listaRutas: arrayUnion(newRouteGroupRef.id)
        });
      }

      // Agregar el nuevo grupo de rutas al batch
      batch.set(newRouteGroupRef, routeGroupData);

      // Agregar las rutas al nuevo grupo
      const routesRef = collection(newRouteGroupRef, 'routes');
      allRoutes.forEach(route => {
        const newRouteRef = doc(routesRef);
        batch.set(newRouteRef, {
          clientId: route.clientId,
          status: route.status,
          order: route.order,
          fecha_pedido: route.fecha_pedido ? Timestamp.fromDate(route.fecha_pedido) : null // Handle undefined
        });
      });

      // Agregar las uniones de rutas al nuevo grupo
      // Create routesUnion subcollection
      const routesUnionRef = collection(newRouteGroupRef, 'routesUnion');
      const unionDoc = doc(routesUnionRef); // Create a new document in routesUnion subcollection

      // Add the routesUnionData to the batch
      batch.set(unionDoc, {
        routes: allRoutes,
        date: Timestamp.fromDate(date),
        isUnificationFlag: true
      });

      // Commit the batch
      await batch.commit();
      // alert('Ruta unificada creada exitosamente');
    } catch (error) {
      // console.error('Error creating unified route group:', error);
      // alert('Error al crear la ruta unificada');
    } finally {
      setSelectedRouteGroups([]);
      setIsUnificationDialogOpen(false);
      setCreateShowNotification(false);
      window.location.reload();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', typography: 'body1' }}>

      {showNotification && (
        <>
          <DarkOverlay />
          <NotificationPopup>
            <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
            <Typography
              sx={{
                color: '#1a1a1a',
                fontWeight: 600,
                fontSize: '1.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              Actualizando Rutas Disponibles
            </Typography>
            <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
          </NotificationPopup>
        </>
      )}

      {createShowNotification && (
        <>
          <DarkOverlay />
          <NotificationPopup>
            <AnimatedEmoji style={{ animationDelay: '0s' }}>✨</AnimatedEmoji>
            <Typography
              sx={{
                color: '#1a1a1a',
                fontWeight: 600,
                fontSize: '1.5rem',
                letterSpacing: '-0.02em',
              }}
            >
              Creando Nueva Ruta Unificada
            </Typography>
            <AnimatedEmoji style={{ animationDelay: '0.2s' }}>🎉</AnimatedEmoji>
          </NotificationPopup>
        </>
      )}

      {viewMode === 0 ? (
        <Box sx={{ padding: '24px' }}>
          {multiRouteDays.length === 0 ? (
            <Typography
              variant="subtitle1"
              color="text.secondary"
              align="center"
              sx={{ my: 4 }}
            >
              No hay rutas de unificación disponibles
            </Typography>
          ) : (
            <Box sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexShrink: 0,
                color: 'rgb(0, 180, 216)'
              }}>
                <h1>Creación de Ruta Unificada</h1>
              </Box>
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: 'rgb(0, 180, 216)',
                margin: '20px 0'
              }} />
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    lineHeight: 1.6,
                    flex: 1
                  }}
                >
                  Selecciona una fecha (día) para unificar las rutas disponibles en esa fecha.
                </Typography>

                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  ml: 2
                }}>
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 0.5
                  }}>
                    <Box component="span" sx={{ color: 'warning.main', mr: 0.5 }}>*</Box>
                    <Typography variant="body2" color="text.secondary">
                      Solo se mostrarán las fechas con más de una ruta disponible.
                    </Typography>
                  </Box>

                  <Typography
                    variant="body2"
                    sx={{
                      fontStyle: 'italic',
                      color: 'text.secondary'
                    }}
                  >
                    Nota: Se contempla por defecto el rango de un mes
                  </Typography>
                </Box>
              </Box>
              <Tabs
                value={currentRouteGroupIndex}
                onChange={handleRouteGroupChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="route groups tabs"
                sx={{
                  mb: 2,
                  bgcolor: 'background.paper', // Fondo de las pestañas
                  borderRadius: 2, // Bordes redondeados
                  boxShadow: 1, // Sombra para un efecto elevado
                  '& .MuiTab-root': {
                    color: '#87CEEB', // Color de texto por defecto
                    '&.Mui-selected': {
                      color: 'white', // Color de texto cuando está seleccionado
                      backgroundColor: '#5CACEE !important', // Fondo cuando está seleccionado
                      borderRadius: 2, // Bordes redondeados para la pestaña seleccionada
                    },
                    '&:hover': {
                      backgroundColor: 'rgba(92, 202, 238, 0.2)', // Efecto hover
                    },
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#5CACEE', // Color del indicador
                  },
                }}
              >
                {multiRouteDays
                  .filter(dayData => {
                    const routeGroupsOnSameDate = dayData.routeGroups.filter(routeGroup => {
                      const safeRoutesUnion = Array.isArray(routeGroup.routesUnion)
                        ? routeGroup.routesUnion
                        : [];

                      const routesForGroup = safeRoutesUnion.filter(routeUnion => {
                        const processedRouteUnion = processRouteUnion(routeUnion);
                        if (!processedRouteUnion) return false;

                        const [_, date, isUnificationFlag] = processedRouteUnion;
                        const routeDate = date instanceof Timestamp ? date.toDate() : date;

                        return (
                          routeDate.toISOString().split('T')[0] === dayData.date.toISOString().split('T')[0] &&
                          isUnificationFlag === true
                        );
                      });

                      return routesForGroup.length > 0;
                    });

                    return routeGroupsOnSameDate.length > 1; // Filtrar solo si hay más de un RouteGroup en la misma fecha
                  })
                  .map((dayData, index) => (
                    <Tab
                      key={index}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">
                            {dayData.date.toLocaleDateString('es-CR', {
                              weekday: 'long',
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </Typography>
                        </Box>
                      }
                      onClick={() => {
                        setIsSaveButtonEnabled(true);
                        setSelectedTabDate(dayData.date);
                      }} // Agregado para establecer la fecha seleccionada
                      {...a11yProps(index)}
                      sx={{
                        textTransform: 'none',
                        fontWeight: 'bold',
                        color: 'text.secondary',
                        '&.Mui-selected': {
                          color: 'primary.main',
                          fontWeight: 'bold',
                        },
                      }}
                    />
                  ))
                }
              </Tabs>
            </Box>
          )}


          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, mt: 4 }}>
            {/* <FormControlLabel
          control={
            <Switch
              checked={viewMode === 1}
              onChange={() => setViewMode(viewMode === 0 ? 1 : 0)}
              color="primary"
              sx={{
                '&.MuiSwitch-root': {
                  '& .MuiSwitch-thumb': {
                    backgroundColor: 'rgb(135, 206, 235) !important',
                  }
                },
              }}
            />
          }
          label={
            <span style={{ fontWeight: 'bold' }}>
              {viewMode === 0 ? "Vista de Rutas Unificadas en Calendario" : "Vista Rutas Unificadas en Base de Datos"}
            </span>
          }
          sx={{ mb: 2, marginBottom: '21px' }}
        /> */}

            <Button
              startIcon={<AddIcon />}
              color="primary"
              onClick={() => {
                fetchAvailableRouteGroups();
                setIsUnificationDialogOpen(true);
              }}
              sx={{
                color: 'white',
                backgroundColor: '#87CEEB', // Color de fondo atractivo
                borderRadius: '8px', // Bordes redondeados
                padding: '10px', // Espaciado interno
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)', // Sombra para efecto de elevación
                '&:hover': {
                  backgroundColor: '#5CACEE', // Color de fondo al pasar el mouse
                  boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.3)', // Sombra más intensa al pasar el mouse
                },
                '&:active': {
                  backgroundColor: '#4DA3D1', // Color de fondo al hacer clic
                  transform: 'scale(0.95)', // Efecto de pulsación al hacer clic
                },
                fontWeight: 'bold',
                transition: 'background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease', // Transiciones suaves
              }}
              disabled={!isSaveButtonEnabled} // Disable button based on the new state
            >
              Crear Ruta Unificada
            </Button>
            <Button
              onClick={handleOpenRefreshDialog}
              color="primary"
              sx={{
                color: 'white',
                backgroundColor: '#87CEEB', // Color de fondo atractivo
                borderRadius: '8px', // Bordes redondeados
                padding: '10px', // Espaciado interno
                boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)', // Sombra para efecto de elevación
                '&:hover': {
                  backgroundColor: '#5CACEE', // Color de fondo al pasar el mouse
                  boxShadow: '0px 6px 12px rgba(0, 0, 0, 0.3)', // Sombra más intensa al pasar el mouse
                },
                '&:active': {
                  backgroundColor: '#4DA3D1', // Color de fondo al hacer clic
                  transform: 'scale(0.95)', // Efecto de pulsación al hacer clic
                },
                fontWeight: 'bold',
                transition: 'background-color 0.3s ease, transform 0.2s ease, box-shadow 0.3s ease', // Transiciones suaves
              }}
              title="Actualizar datos" // Tooltip para mejorar la accesibilidad
            >
              <PlaylistAddIcon fontSize="large" sx={{ mr: '6px', fontSize: '1.5rem' }} /> {/* Ícono más grande */}
              Actualizar Rutas Disponibles
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mt: 4 }}>
            Listado de rutas disponibles para unificar
          </Typography>

          {multiRouteDays
            .filter(dayData => {
              const routeGroupsOnSameDate = dayData.routeGroups.filter(routeGroup => {
                const safeRoutesUnion = Array.isArray(routeGroup.routesUnion)
                  ? routeGroup.routesUnion
                  : [];

                const routesForGroup = safeRoutesUnion.filter(routeUnion => {
                  const processedRouteUnion = processRouteUnion(routeUnion);
                  if (!processedRouteUnion) return false;

                  const [_, date, isUnificationFlag] = processedRouteUnion;
                  const routeDate = date instanceof Timestamp ? date.toDate() : date;

                  return (
                    routeDate.toISOString().split('T')[0] === dayData.date.toISOString().split('T')[0] &&
                    isUnificationFlag === true
                  );
                });

                return routesForGroup.length > 0;
              });

              return routeGroupsOnSameDate.length > 1; // Filtrar solo si hay más de un RouteGroup en la misma fecha
            })
            .map((dayData, index) => (
              <TabPanel
                key={index}
                value={currentRouteGroupIndex}
                index={index}
              >
                <Grid container spacing={2}>
                  {dayData.routeGroups
                    .filter(routeGroup => {
                      const safeRoutesUnion = Array.isArray(routeGroup.routesUnion)
                        ? routeGroup.routesUnion
                        : [];

                      const routesForGroup = (() => {
                        const matchingRouteUnion = safeRoutesUnion.find(
                          (routeUnion: any) => {
                            const processedRouteUnion = processRouteUnion(routeUnion);
                            if (!processedRouteUnion) return false;

                            const [_, date, isUnificationFlag] = processedRouteUnion;
                            const routeDate = date instanceof Timestamp ? date.toDate() : date;

                            return (
                              routeDate.toISOString().split('T')[0] === dayData.date.toISOString().split('T')[0] &&
                              isUnificationFlag === true
                            );
                          }
                        );

                        return matchingRouteUnion
                          ? processRouteUnion(matchingRouteUnion)?.[0] || []
                          : [];
                      })();

                      return routesForGroup.length > 0;
                    })
                    .map((routeGroup, index) => {
                      const safeRoutesUnion = Array.isArray(routeGroup.routesUnion)
                        ? routeGroup.routesUnion
                        : [];

                      const routesForGroup = (() => {
                        const matchingRouteUnion = safeRoutesUnion.find(
                          (routeUnion: any) => {
                            const processedRouteUnion = processRouteUnion(routeUnion);
                            if (!processedRouteUnion) return false;

                            const [_, date, isUnificationFlag] = processedRouteUnion;
                            const routeDate = date instanceof Timestamp ? date.toDate() : date;

                            return (
                              routeDate.toISOString().split('T')[0] === dayData.date.toISOString().split('T')[0] &&
                              isUnificationFlag === true
                            );
                          }
                        );

                        return matchingRouteUnion
                          ? processRouteUnion(matchingRouteUnion)?.[0] || []
                          : [];
                      })();

                      return (
                        <Grid item key={`${routeGroup.id}-${index}`} xs={12} sm={6} md={4}>
                          <Card
                            elevation={3} // Change from variant="outlined" to elevation for shadow effect
                            sx={{
                              p: 3, // Padding similar to ClientList
                              borderRadius: 2, // Rounded corners
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              position: 'relative',
                              transition: 'box-shadow 0.3s', // Transition for box-shadow
                              minHeight: '329px', // Set a minimum height
                              // '&:hover': {
                              //   boxShadow: '0px 8px 30px rgba(135, 206, 235, 0.7)', // Intense shadow on hover
                              // },
                            }}

                          // onClick={() => handleRouteGroupSelect(routeGroup)}
                          >
                            <CardContent sx={{ flexGrow: 1 }}>
                              {/* {selectedRouteGroups.some(rg => rg.id === routeGroup.id) && (
                                <Box
                                  sx={{
                                    position: 'absolute',
                                    top: 8,
                                    right: 8,
                                    width: 24,
                                    height: 24,
                                    borderRadius: '50%',
                                    backgroundColor: 'primary.main',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontWeight: 'bold'
                                  }}
                                >
                                  ✓
                                </Box>
                              )} */}
                              <Typography
                                variant="h6"
                                color="primary"
                                gutterBottom
                                sx={{
                                  fontWeight: 'bold',
                                  opacity: selectedRouteGroups.some(rg => rg.routeGroup.id === routeGroup.id) ? 1 : 0.7
                                }}
                              >
                                {routeGroup.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                  mb: 1,
                                  opacity: selectedRouteGroups.some(rg => rg.routeGroup.id === routeGroup.id) ? 1 : 0.7
                                }}
                              >
                                Total de Pedidos: {routesForGroup?.length ?? 0}
                              </Typography>
                              <Box
                                sx={{
                                  display: 'flex',
                                  flexWrap: 'wrap',
                                  gap: 1,
                                  mt: 'auto' // Push to bottom of card
                                }}
                              >
                                {(routesForGroup || []).map((route, routeIndex) => {
                                  // Get client name, fallback to generic route label
                                  const client = route.clientId
                                    ? clientsMap.get(route.clientId)
                                    : null;
                                  const chipLabel = client
                                    ? client.name || `Cliente ${routeIndex + 1}`
                                    : `Ruta ${routeIndex + 1}`;

                                  return (
                                    <Chip
                                      key={route.id || routeIndex}
                                      label={chipLabel}
                                      size="small"
                                      color={selectedRouteGroups.some(rg => rg.routeGroup.id === routeGroup.id) ? 'primary' : 'default'}
                                      variant="outlined"
                                    />
                                  );
                                })}
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                </Grid>
              </TabPanel>
            ))
          }
        </Box>
      ) : (
        <Box>
          {/* Detailed View - To be implemented */}
          <Typography
            variant="subtitle1"
            color="text.secondary"
            align="center"
            sx={{ my: 4 }}
          >
            Vista Detallada (Próximamente)
          </Typography>
        </Box>
      )}

      {/* Unification Dialog */}
      <Dialog
        open={isUnificationDialogOpen}
        onClose={() => {
          setIsUnificationDialogOpen(false);
          setSelectedRouteGroups([]); // Clear selected route groups
        }}
        maxWidth="xl"
        fullWidth
      >
        <DialogTitle sx={{
          backgroundColor: '#87CEEB',
          color: 'white',
          fontWeight: 'bold'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Crear Nueva Ruta Unificada</Typography>
            {/* <IconButton onClick={() => setIsUnificationDialogOpen(false)}>
              <CloseIcon />
            </IconButton> */}
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid item xs={12} sx={{ mb: 3, mt: 3 }}>
            <StyledFormControl fullWidth>
              <Autocomplete
                options={transportistas}
                value={selectedTransportista}
                onChange={(_, newValue) => {
                  setSelectedTransportista(newValue);
                  // You can add additional logic here if needed for the unification process
                  if (newValue) {
                    // Example: Update state or trigger other actions when a transportista is selected
                    setIsSaveButtonEnabled(true); // Enable save button when transportista is selected
                  } else {
                    setIsSaveButtonEnabled(false); // Disable save button when no transportista is selected
                  }
                }}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Asignar Transportista"
                    variant="outlined"
                    placeholder="Buscar transportista..."
                    InputLabelProps={{
                      sx: {
                        backgroundColor: 'white',
                        padding: '0 4px',
                        fontSize: '1rem',
                        fontWeight: 'bold',
                        color: '#87CEEB',
                        transition: 'all 0.2s ease'
                      }
                    }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <PersonIcon sx={{ color: '#87CEEB', mr: 1 }} />
                      ),
                      sx: {
                        '& fieldset': {
                          borderColor: '#87CEEB',
                          borderWidth: '2px',
                          borderRadius: '8px',
                          transition: 'all 0.2s ease'
                        },
                        '&:hover fieldset': {
                          borderColor: '#5CACEE',
                          borderWidth: '2px'
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#87CEEB',
                          borderWidth: '2px'
                        },
                        backgroundColor: 'white'
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box
                    component="li"
                    {...props}
                    sx={{
                      padding: '12px 16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(135, 206, 235, 0.08)',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 2px 8px rgba(135, 206, 235, 0.15)'
                      },
                      '&.Mui-focused': {
                        backgroundColor: 'rgba(135, 206, 235, 0.12)',
                        '&:hover': {
                          backgroundColor: 'rgba(135, 206, 235, 0.16)'
                        }
                      }
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: '#87CEEB',
                        width: 32,
                        height: 32,
                        fontSize: '1rem',
                        transition: 'transform 0.2s ease',
                        '&:hover': {
                          transform: 'scale(1.1)'
                        }
                      }}
                    >
                      {option.name.charAt(0)}
                    </Avatar>
                    <Typography sx={{
                      fontWeight: 500,
                      color: '#2c3e50'
                    }}>
                      {option.name}
                    </Typography>
                  </Box>
                )}
                noOptionsText={
                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 1,
                    py: 2
                  }}>
                    <SearchIcon sx={{ color: '#7f8c8d' }} />
                    <Typography sx={{
                      color: '#7f8c8d',
                      fontWeight: 500
                    }}>
                      No se encontraron transportistas
                    </Typography>
                  </Box>
                }
                sx={{
                  '& .MuiAutocomplete-endAdornment': {
                    color: '#87CEEB'
                  }
                }}
              />
            </StyledFormControl>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {multiRouteDays
              .filter(dayData => {
                // Filtrar solo el día que coincide con selectedTabDate
                return selectedTabDate && dayData.date.toDateString() === selectedTabDate.toDateString();
              })
              .map((dayData, index) => (
                <Grid item key={index} xs={12}>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 2,
                      fontWeight: 'bold',
                      color: 'primary.main'
                    }}
                  >
                    {dayData.date.toLocaleDateString('es-ES', {
                      weekday: 'long',
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                    Selecciona las Rutas Disponibles que deseas Unificar
                  </Typography>

                  <Grid container spacing={2}>
                    {dayData.routeGroups
                      .filter(routeGroup => {
                        const safeRoutesUnion = Array.isArray(routeGroup.routesUnion)
                          ? routeGroup.routesUnion
                          : [];

                        const routesForGroup = (() => {
                          const matchingRouteUnion = safeRoutesUnion.find(
                            (routeUnion: any) => {
                              const processedRouteUnion = processRouteUnion(routeUnion);
                              if (!processedRouteUnion) return false;

                              const [_, date, isUnificationFlag] = processedRouteUnion;
                              const routeDate = date instanceof Timestamp ? date.toDate() : date;

                              return (
                                routeDate.toISOString().split('T')[0] === selectedTabDate?.toISOString().split('T')[0] &&
                                isUnificationFlag === true
                              );
                            }
                          );

                          return matchingRouteUnion
                            ? processRouteUnion(matchingRouteUnion)?.[0] || []
                            : [];
                        })();

                        return routesForGroup.length > 0;
                      })
                      .map((routeGroup, index) => {
                        const safeRoutesUnion = Array.isArray(routeGroup.routesUnion)
                          ? routeGroup.routesUnion
                          : [];

                        const routesForGroup = (() => {
                          const matchingRouteUnion = safeRoutesUnion.find(
                            (routeUnion: any) => {
                              const processedRouteUnion = processRouteUnion(routeUnion);
                              if (!processedRouteUnion) return false;

                              const [_, date, isUnificationFlag] = processedRouteUnion;
                              const routeDate = date instanceof Timestamp ? date.toDate() : date;

                              return (
                                routeDate.toISOString().split('T')[0] === selectedTabDate?.toISOString().split('T')[0] &&
                                isUnificationFlag === true
                              );
                            }
                          );

                          return matchingRouteUnion
                            ? processRouteUnion(matchingRouteUnion)?.[0] || []
                            : [];
                        })();

                        return (
                          <Grid item key={`${routeGroup.id}-${index}`} xs={12} sm={6} md={4}>
                            <Card
                              variant="outlined"
                              elevation={3}
                              sx={{
                                p: 3,
                                borderRadius: 2,
                                alignItems: 'flex-start',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'box-shadow 0.3s',
                                minHeight: '329px',
                                '&:hover': {
                                  boxShadow: '0px 8px 30px rgba(135, 206, 235, 0.7)',
                                  transform: 'scale(1.02)',
                                },
                                cursor: 'pointer',
                                position: 'relative',
                                border: selectedRouteGroups.some(selected =>
                                  selected.routeGroup.id === routeGroup.id && selected.dateUnification.toDateString() === selectedTabDate?.toDateString() &&
                                  selected.routeGroup.routesUnion.some(([_, routeDate]) => {
                                    const normalizedRouteDate = routeDate instanceof Timestamp ? routeDate.toDate() : routeDate;
                                    return normalizedRouteDate && normalizedRouteDate.toDateString() === selected.dateUnification.toDateString();
                                  })
                                )
                                  ? '2px solid primary.main'
                                  : '1px solid rgba(0,0,0,0.12)',
                                backgroundColor: selectedRouteGroups.some(selected =>
                                  selected.routeGroup.id === routeGroup.id && selected.dateUnification.toDateString() === selectedTabDate?.toDateString() &&
                                  selected.routeGroup.routesUnion.some(([_, routeDate]) => {
                                    const normalizedRouteDate = routeDate instanceof Timestamp ? routeDate.toDate() : routeDate;
                                    return normalizedRouteDate && normalizedRouteDate.toDateString() === selected.dateUnification.toDateString();
                                  })
                                )
                                  ? 'rgba(0, 123, 255, 0.05)'
                                  : 'white',
                                boxShadow: selectedRouteGroups.some(selected =>
                                  selected.routeGroup.id === routeGroup.id && selected.dateUnification.toDateString() === selectedTabDate?.toDateString() &&
                                  selected.routeGroup.routesUnion.some(([_, routeDate]) => {
                                    const normalizedRouteDate = routeDate instanceof Timestamp ? routeDate.toDate() : routeDate;
                                    return normalizedRouteDate && normalizedRouteDate.toDateString() === selected.dateUnification.toDateString();
                                  })
                                )
                                  ? '0 4px 6px rgba(0,123,255,0.1)'
                                  : 'none',
                              }}
                              onClick={() => handleRouteGroupSelect(routeGroup, selectedTabDate!)}
                            >
                              <CardContent
                                sx={{
                                  flexGrow: 1,
                                  display: 'flex',
                                  flexDirection: 'column',
                                  minHeight: 250
                                }}
                              >
                                {selectedRouteGroups.some(selected =>
                                  selected.routeGroup.id === routeGroup.id &&
                                  selected.dateUnification.toDateString() === selectedTabDate?.toDateString()
                                ) && (
                                    <Box
                                      sx={{
                                        position: 'absolute',
                                        top: 8,
                                        right: 8,
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        backgroundColor: 'primary.main',
                                        color: 'white',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold'
                                      }}
                                    >
                                      ✓
                                    </Box>
                                  )}
                                <Typography
                                  variant="h6"
                                  color="primary"
                                  gutterBottom
                                  sx={{
                                    fontWeight: 'bold',
                                    opacity: selectedRouteGroups.some(selected =>
                                      selected.routeGroup.id === routeGroup.id &&
                                      selected.dateUnification.toDateString() === selectedTabDate?.toDateString()
                                    ) ? 1 : 0.7,
                                    wordBreak: 'break-word',
                                    mb: 1
                                  }}
                                >
                                  {routeGroup.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{
                                    mb: 2,
                                    opacity: selectedRouteGroups.some(selected =>
                                      selected.routeGroup.id === routeGroup.id &&
                                      selected.dateUnification.toDateString() === selectedTabDate?.toDateString()
                                    ) ? 1 : 0.7
                                  }}
                                >
                                  Total de Pedidos: {routesForGroup?.length ?? 0}
                                </Typography>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    gap: 1,
                                    mt: 'auto'
                                  }}
                                >
                                  {(routesForGroup || []).map((route, routeIndex) => {
                                    const client = route.clientId
                                      ? clientsMap.get(route.clientId)
                                      : null;
                                    const chipLabel = client
                                      ? client.name || `Cliente ${routeIndex + 1}`
                                      : `Ruta ${routeIndex + 1}`;

                                    return (
                                      <Chip
                                        key={route.id || routeIndex}
                                        label={chipLabel}
                                        size="small"
                                        color={selectedRouteGroups.some(selected =>
                                          selected.routeGroup.id === routeGroup.id &&
                                          selected.dateUnification.toDateString() === selectedTabDate?.toDateString()
                                        ) ? 'primary' : 'default'}
                                        variant="outlined"
                                      />
                                    );
                                  })}
                                </Box>
                              </CardContent>
                            </Card>
                          </Grid>
                        );
                      })}
                  </Grid>
                </Grid>
              ))}
          </Grid>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'right',
          px: 3,
          py: 2,
          backgroundColor: 'rgba(135, 206, 235, 0.1)'
        }}>
          <Button onClick={() => selectedDayData && createUnifiedRouteGroup(selectedDayData.date)} variant="contained"
            disabled={selectedRouteGroups.length < 2 || !selectedTransportista}
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5CACEE',
              },
              '&:disabled': {
                backgroundColor: 'rgba(135, 206, 235, 0.5)',
                color: 'rgba(255, 255, 255, 0.7)',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}>
            Unificar Ruta
          </Button>
          <Button
            onClick={() => {
              setIsUnificationDialogOpen(false);
              setSelectedRouteGroups([]); // Clear selected route groups
            }}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Refresh Routes Dialog */}
      <Dialog open={isRefreshRoutesDialogOpen} onClose={handleCloseRefreshDialog}>
        <DialogTitle sx={{
          backgroundColor: '#87CEEB',
          color: 'white',
          fontWeight: 'bold'
        }}>Seleccionar Rango de Fechas</DialogTitle>
        <DialogContent sx={{ mt: 2, minWidth: 600, px: 4, py: 3 }}>
          <Box sx={{ width: '100%' }}>
            {/* Title */}
            <Typography variant="h5" sx={{
              mb: 4,
              color: 'rgb(0, 180, 216)',
              textAlign: 'center',
              fontWeight: 600,
              letterSpacing: '-0.02em'
            }}>
              Actualización de Rutas Automáticas
            </Typography>

            {/* Info Cards */}
            <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
              {/* Info Card 1 */}
              <Paper elevation={0} sx={{
                flex: 1,
                p: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 180, 216, 0.04)',
                border: '1px solid rgba(0, 180, 216, 0.1)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <CalendarTodayIcon sx={{ color: 'rgb(0, 180, 216)', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: 'rgb(0, 180, 216)', fontWeight: 600, fontSize: '1rem' }}>
                    Rango de Fechas
                  </Typography>
                </Box>
                <Typography sx={{
                  color: '#666',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}>
                  Seleccione el rango de fechas para actualizar las rutas disponibles.
                </Typography>
              </Paper>

              {/* Info Card 2 */}
              <Paper elevation={0} sx={{
                flex: 1,
                p: 3,
                borderRadius: 2,
                backgroundColor: 'rgba(0, 180, 216, 0.04)',
                border: '1px solid rgba(0, 180, 216, 0.1)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <AutorenewIcon sx={{ color: 'rgb(0, 180, 216)', mr: 1 }} />
                  <Typography variant="h6" sx={{ color: 'rgb(0, 180, 216)', fontWeight: 600, fontSize: '1rem' }}>
                    Actualización
                  </Typography>
                </Box>
                <Typography sx={{
                  color: '#666',
                  fontSize: '0.9rem',
                  lineHeight: 1.6,
                }}>
                  Se actualizarán todas las rutas disponibles en el rango seleccionado.
                </Typography>
              </Paper>
            </Box>

            {/* Date Picker Section */}
            <Box sx={{
              display: 'flex',
              gap: 4,
              justifyContent: 'center',
              alignItems: 'flex-start',
              backgroundColor: '#fff',
              p: 4,
              borderRadius: 2,
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)',
            }}>
              {/* Start Date */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  mb: 1.5,
                  color: 'rgb(0, 180, 216)',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}>
                  Fecha de Inicio
                </Typography>
                <DatePicker
                  selected={refreshDateRange[0]}
                  onChange={(date) => setRefreshDateRange([date, refreshDateRange[1]])}
                  dateFormat="dd/MM/yyyy"
                  customInput={
                    <TextField
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          transition: 'all 0.2s ease',
                          '& fieldset': { borderColor: 'rgba(0, 180, 216, 0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(0, 180, 216, 0.5)' },
                          '&.Mui-focused fieldset': { borderColor: 'rgb(0, 180, 216)' },
                          input: {
                            color: 'rgb(0, 180, 216)',
                            fontWeight: 500,
                            textAlign: 'center',
                            padding: '12px',
                            fontSize: '0.95rem'
                          }
                        }
                      }}
                    />
                  }
                  locale={es}
                  calendarClassName="custom-calendar"
                />
              </Box>

              {/* End Date */}
              <Box sx={{ flex: 1 }}>
                <Typography sx={{
                  mb: 1.5,
                  color: 'rgb(0, 180, 216)',
                  fontWeight: 600,
                  fontSize: '0.95rem'
                }}>
                  Fecha Final
                </Typography>
                <DatePicker
                  selected={refreshDateRange[1]}
                  onChange={(date) => setRefreshDateRange([refreshDateRange[0], date])}
                  dateFormat="dd/MM/yyyy"
                  customInput={
                    <TextField
                      variant="outlined"
                      fullWidth
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: '#fff',
                          transition: 'all 0.2s ease',
                          '& fieldset': { borderColor: 'rgba(0, 180, 216, 0.2)' },
                          '&:hover fieldset': { borderColor: 'rgba(0, 180, 216, 0.5)' },
                          '&.Mui-focused fieldset': { borderColor: 'rgb(0, 180, 216)' },
                          input: {
                            color: 'rgb(0, 180, 216)',
                            fontWeight: 500,
                            textAlign: 'center',
                            padding: '12px',
                            fontSize: '0.95rem'
                          }
                        }
                      }}
                    />
                  }
                  locale={es}
                  minDate={refreshDateRange[0] || undefined}
                  calendarClassName="custom-calendar"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{
          justifyContent: 'right',
          px: 3,
          py: 2,
          backgroundColor: 'rgba(135, 206, 235, 0.1)'
        }}>
          <Button
            onClick={handleRefresh}
            variant="contained"
            sx={{
              backgroundColor: '#87CEEB',
              color: 'white',
              '&:hover': {
                backgroundColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
            }}>
            Actualizar
          </Button>
          <Button
            onClick={handleCloseRefreshDialog}
            variant="outlined"
            sx={{
              color: '#87CEEB',
              borderColor: '#87CEEB',
              '&:hover': {
                backgroundColor: 'rgba(135, 206, 235, 0.1)',
                borderColor: '#5CACEE',
              },
              fontWeight: 'bold',
              px: 4,
              py: 1,
              mr: 2,
            }}
          >
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box >
  );
};

export default UnificationRoutes;
